import { LineStringGeometry } from 'app/agriculture/queries/types/geometry/LineStringGeometry';
import { PolygonGeometry } from 'app/agriculture/queries/types/geometry/PolygonGeometry';
import * as Cesium from 'cesium';
import { Ellipsoid, Math as CesiumMath } from 'cesium';
import { Position } from 'app/agriculture/queries/types/geometry/Position';
import { area, kinks, length, lineString, polygon } from '@turf/turf';
import { MapEntity } from '../../../queries/types/geometry/MapEntity';
import { InfraType } from '../../../mutations/types/entityConfig';

// 1000 km
const MAX_INFRA_LINE_LENGTH_KM = 1000;
// 100,000 hectares
const MAX_INFRA_POLY_AREA_SQM = 1_000_000_000;
const SQM_PER_HECTARE = 10_000;
const MIN_POLYGON_POINTS = 3;
const MIN_LINESTRING_POINTS = 2;

export function geometryToCartesian3Array({
    type,
    coordinates,
}: LineStringGeometry | PolygonGeometry): Cesium.Cartesian3[] {
    switch (type) {
        case 'LineString':
            return coordinates.map((point) => Cesium.Cartesian3.fromDegrees(point[0], point[1]));
        case 'Polygon':
            if (coordinates[0] != null) {
                return coordinates[0].map((point) =>
                    Cesium.Cartesian3.fromDegrees(point[0], point[1]),
                );
            }
            return [];
    }
}

export function cartesian3ToGeometryCoordinates(
    value: Cesium.Cartesian3[],
    geometryType: 'LineString' | 'Polygon',
) {
    const array = value.map((point) => {
        const location = Ellipsoid.WGS84.cartesianToCartographic(point);
        return [
            CesiumMath.toDegrees(location.longitude),
            CesiumMath.toDegrees(location.latitude),
        ] satisfies Position;
    });

    switch (geometryType) {
        case 'LineString':
            return array;
        case 'Polygon':
            return [array];
    }
}

export function computeHalfHandles(positions: Cesium.Cartesian3[]): Cesium.Cartesian3[] {
    if (positions.length < 2) return []; // Prevents issues with empty or single-point arrays

    const result: Cesium.Cartesian3[] = [];
    for (let i = 0; i < positions.length - 1; i++) {
        const nextPos = positions[i + 1];
        const currentPos = positions[i];
        if (nextPos == null || currentPos == null) break;
        result.push(Cesium.Cartesian3.midpoint(currentPos, nextPos, new Cesium.Cartesian3()));
    }
    return result;
}

export const geometryEmpty = (geometry?: MapEntity['geometry']) => {
    if (!geometry) return;
    const { coordinates, type } = geometry;
    switch (type) {
        case 'Point': {
            const [point] = coordinates;
            if (!point) return 'Please select a location on the map.';
            break;
        }
        case 'LineString': {
            if (isEmpty(coordinates)) return 'Please select a location on the map.';
            break;
        }
        case 'Polygon': {
            const [ring] = coordinates;
            if (isEmpty(ring)) return 'Please select a location on the map.';
            break;
        }
    }
};

const isEmpty = (points: Position[] | undefined): boolean => {
    return !points || points.length === 0;
};

export const selfIntersections = (infraType: InfraType, geometry?: MapEntity['geometry']) => {
    if (!geometry) return;
    const { coordinates, type } = geometry;
    if (type === 'Polygon') {
        const [points] = coordinates;
        if (!points || points.length === 0 || points[0] === undefined) return;
        // Check the open polygon isn't intersecting
        if (selfIntersectingLineString(points)) {
            return `The ${infraType.toLowerCase()} cannot intersect itself.`;
        }
        // Check automatically closing the open shape won't cause an intersection (to cover things like S shapes)
        if (autoCloseWouldCauseIntersection(points)) {
            return `Closing the ${infraType.toLowerCase()} would cause an intersection.`;
        }
    }
    return undefined;
};

export const minPoints = (infraType: InfraType, geometry?: MapEntity['geometry']) => {
    if (!geometry) return;
    const { coordinates, type } = geometry;
    if (type === 'Polygon') {
        const [points] = coordinates;
        if (!points || points.length === 0 || points[0] === undefined) return;
        if (points.length < MIN_POLYGON_POINTS)
            return `The ${infraType.toLowerCase()} must have at least three points.`;
    }
    if (type === 'LineString') {
        if (coordinates.length < MIN_LINESTRING_POINTS)
            return `The ${infraType.toLowerCase()} must have at least two points.`;
    }
};

export const tooLarge = (infraType: InfraType, geometry?: MapEntity['geometry']) => {
    if (!geometry) return;
    const { coordinates, type } = geometry;
    if (type === 'Polygon') {
        const [points] = coordinates;
        if (!points || points.length === 0 || points[0] === undefined) return;
        return getPolygonAreaError(closePolygon(points), infraType);
    }
};

export const tooLong = (infraType: InfraType, geometry?: MapEntity['geometry']) => {
    if (!geometry) return;
    const { coordinates, type } = geometry;
    if (type !== 'LineString') return;
    return getLineLengthError(coordinates, infraType);
};

const polygonClosed = (points: Position[]) => {
    const [firstPoint] = points;
    const lastPoint = points[points.length - 1];
    if (firstPoint === undefined || lastPoint === undefined) return false;
    const [firstLng, firstLat] = firstPoint;
    const [lastLng, lastLat] = lastPoint;
    return firstLng === lastLng && firstLat === lastLat;
};

const closePolygon = (points: Position[]) => {
    const [firstPoint] = points;
    if (polygonClosed(points) || !firstPoint) return points;
    return [...points, firstPoint];
};

const getLineLengthError = (line: Position[], infraType: InfraType) => {
    const lengthKms = length(lineString(line));
    return lengthKms > MAX_INFRA_LINE_LENGTH_KM
        ? `The ${infraType.toLowerCase()} of ${lengthKms.toLocaleString(undefined, { maximumFractionDigits: 0 })} km exceeds the max length ${MAX_INFRA_LINE_LENGTH_KM} km.`
        : undefined;
};

const getPolygonAreaError = (points: Position[], infraType: InfraType) => {
    const areaSqM = area(polygon([points]));
    const areaHa = areaSqM / SQM_PER_HECTARE;
    const maxHa = MAX_INFRA_POLY_AREA_SQM / SQM_PER_HECTARE;
    return areaSqM > MAX_INFRA_POLY_AREA_SQM
        ? `The ${infraType.toLowerCase()} of ${areaHa.toLocaleString(undefined, { maximumFractionDigits: 0 })} ha exceeds the max area of ${maxHa.toLocaleString(undefined, { maximumFractionDigits: 0 })} ha.`
        : undefined;
};

const selfIntersectingLineString = (points: Position[]) =>
    kinks(lineString(points)).features.length > 0;

const selfIntersectingPolygon = (points: Position[]) =>
    kinks(polygon([points])).features.length > 0;

const autoCloseWouldCauseIntersection = (points: Position[]) =>
    selfIntersectingPolygon(closePolygon(points));

export const finaliseGeometry = (geometry: MapEntity['geometry']): MapEntity['geometry'] => {
    const { type, coordinates } = geometry;
    if (type !== 'Polygon') return geometry;
    const [points] = coordinates;
    if (!points || points.length === 0) return geometry;
    return {
        type: 'Polygon',
        coordinates: [closePolygon(points)],
    };
};
